import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/format";

/**
 * Per-game analytics table — shared between Admin and Merchant dashboards.
 * Expects `games` = array from `GET /api/analytics/per-game`.
 */
export default function PerGameTable({ games }) {
  const { t } = useTranslation();

  if (!games || games.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        {t("dashboard.no_data")}
      </div>
    );
  }

  // Totals row
  const totals = games.reduce(
    (acc, g) => ({
      plays: acc.plays + (g.plays || 0),
      wins: acc.wins + (g.wins || 0),
      leads: acc.leads + (g.leads || 0),
    }),
    { plays: 0, wins: 0, leads: 0 },
  );
  const globalConv = totals.plays
    ? Math.round((totals.wins / totals.plays) * 1000) / 10
    : 0;

  return (
    <div className="overflow-x-auto" data-testid="per-game-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("dashboard.th_game")}</TableHead>
            <TableHead className="text-center">
              {t("dashboard.th_status", "Statut")}
            </TableHead>
            <TableHead className="text-right">
              {t("dashboard.th_plays")}
            </TableHead>
            <TableHead className="text-right">
              {t("dashboard.th_leads", "Leads")}
            </TableHead>
            <TableHead className="text-right">
              {t("dashboard.th_wins")}
            </TableHead>
            <TableHead className="text-right">
              {t("dashboard.th_conversion")}
            </TableHead>
            <TableHead>{t("dashboard.th_last_play", "Dernière activité")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {games.map((g) => {
            const statusColor =
              g.status === "live"
                ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                : g.status === "scheduled"
                  ? "bg-sky-500/15 text-sky-600 dark:text-sky-400"
                  : g.status === "ended"
                    ? "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400"
                    : "bg-amber-500/15 text-amber-600 dark:text-amber-400";
            return (
              <TableRow
                key={g.game_id}
                data-testid={`per-game-row-${g.game_id}`}
              >
                <TableCell className="font-medium">{g.title}</TableCell>
                <TableCell className="text-center">
                  {g.status ? (
                    <Badge
                      className={`${statusColor} border-0 rounded-full text-[10.5px] font-semibold uppercase tracking-wide`}
                    >
                      {t(`status.${g.status}`, g.status)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {g.plays}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {g.leads ?? 0}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {g.wins}
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {g.conversion}%
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {g.last_play ? formatDate(g.last_play) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className="border-t-2 bg-muted/30 font-semibold">
            <TableCell>{t("dashboard.th_total", "Total")}</TableCell>
            <TableCell />
            <TableCell className="text-right tabular-nums">
              {totals.plays}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {totals.leads}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {totals.wins}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {globalConv}%
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
